/**
 * tiscali.cz — TEK sayfa (2026-09-16 operatör isteği): stok, kimlik doğrulama,
 * site başına satışlar ve mailbox başına "hangi siteler için satıldı" detayı,
 * import ve yenile — hepsi burada. (Alias-model şablonu bilinçli olarak
 * kullanılmaz: tiscalicz'da alias yok, satış hakkı (mailbox, site) bazındadır.)
 *
 * 2026-09-19 (operatör kararı): tiscali.cz'de IMAP ve çalışan POP3S yok; posta
 * POP3 port 110 (şifresiz) ile okunur ve ASLA doğrudan değil, residential SOCKS5
 * gateway'i üzerinden bağlanılır. Gateway bu sayfadan düzenlenir (ortak kart,
 * `protocol="POP3"` → "POP3 Proxy (SOCKS5)"); kullanıcı adındaki `{session}`
 * her kutuya sabit çıkış IP'si verir.
 */
import { useCallback, useEffect, useState } from 'react';

import { useCan } from '../../session/can.js';
import { BulkImportModal } from '../mailcom/BulkImportModal.js';
import { MailboxUnitManager } from '../mailbox-unit/MailboxUnitManager.js';
import { ImapGatewayCard } from '../mailbox-unit/ImapGatewayCard.js';
import {
  availableShare,
  fetchTiscaliczSiteUsage,
  type TiscaliczSiteUsage,
  type TiscaliczSiteUsageRow,
} from './siteUsage.js';

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ok'; readonly data: TiscaliczSiteUsage }
  | { readonly kind: 'error'; readonly message: string };


function fmt(ts: string | null | undefined): string {
  return ts ? new Date(ts).toLocaleString('tr-TR') : '—';
}

export function TiscaliczPage(): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const canManage = useCan('provider:manage_inventory');
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async (signal?: AbortSignal) => {
    setState({ kind: 'loading' });
    const res = await fetchTiscaliczSiteUsage(signal);
    if (res.kind === 'ok') {
      setState({ kind: 'ok', data: res.data });
      return;
    }
    if (res.kind === 'auth_required') {
      setState({ kind: 'error', message: 'Oturum gerekli — lütfen admin girişi yapın.' });
      return;
    }
    if (res.kind === 'forbidden') {
      setState({ kind: 'error', message: 'Yetki yok: provider:inspect izni gerekiyor.' });
      return;
    }
    if (res.kind === 'network_error') {
      setState({ kind: 'error', message: 'Bağlantı hatası: ' + res.message });
      return;
    }
    setState({ kind: 'error', message: 'İstek başarısız (' + res.status + ': ' + res.reason + ').' });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);


  return (
    <section className="tiscalicz-dashboard" data-testid="tiscalicz-dashboard">
      <header className="tiscalicz-head">
        <div>
          <h1>tiscali.cz — stok, kimlik ve site kullanımı</h1>
          <p>
            Her satır bir mailbox'tır (alias üretilmez). Satış hakkı{' '}
            <strong>(mailbox, site)</strong> bazındadır: posta gelmezse yalnız o
            müşteriye kapanır ve başka müşteriye satılabilir; posta gelirse çift
            herkese yanar. Bir mailbox, kimliği doğrulanmadan satılmaz.
          </p>
          {/* R-TISCALICZ-GATEWAY (2026-09-19): tiscali.cz has no IMAP and its
              POP3S port fails, so the runtime reads POP3 on port 110 in plaintext
              — always through the residential gateway below, never direct (no
              gateway = no login, fail closed). POP3 must re-login to see new
              mail; the {session} placeholder keeps those re-logins on one exit
              IP per mailbox. */}
          <p data-testid="tiscalicz-transport-note" style={{ marginTop: 6 }}>
            Posta <strong>POP3</strong> (port 110, şifresiz — tiscali.cz IMAP ve çalışan POP3S
            sunmuyor) ile, aşağıdaki <strong>residential proxy gateway'i üzerinden</strong> okunur;
            POP3 yalnız gelen kutusunu görür. Yeni postayı görmek için periyodik yeniden giriş
            yapılır; kullanıcı adında {'{session}'} varsa her kutu aynı çıkış IP'sinde kalır.
            Gateway tanımlı değilse hiç giriş yapılmaz (doğrudan bağlantı yok).
          </p>
        </div>
        <div className="tiscalicz-head-actions">
          {state.kind === 'ok' && (
            <span className="badge badge-neutral">
              {state.data.accounts} hesap · {state.data.units.total} unit
            </span>
          )}
          {canManage ? (
            <button
              type="button"
              className="btn"
              onClick={() => setShowImport(true)}
              data-testid="tiscalicz-import"
              title="email:şifre satırları — mailbox'ın kendisi unit olarak eklenir; şifre ':' içerebilir"
            >
              Import / TXT
            </button>
          ) : null}
          <button type="button" className="btn" onClick={() => void load()} data-testid="tiscalicz-refresh">
            Yenile
          </button>
        </div>
      </header>
      {canManage ? (
        <BulkImportModal
          providerId="tiscalicz"
          providerLabel="tiscali.cz"
          open={showImport}
          onClose={() => setShowImport(false)}
          onCompleted={() => void load()}
        />
      ) : null}

      {/* R-TISCALICZ-GATEWAY (2026-09-19): the POP3 SOCKS5 gateway is editable
          here (shared card, same /imap-gateway endpoint as web.de/t-online.de);
          the saved value is authoritative for the mail runtime (the K8s Secret
          is only the fallback). Rendered outside the site-usage states so the
          operator can fix the proxy even when that read fails. */}
      <ImapGatewayCard providerId="tiscalicz" providerLabel="tiscali.cz" protocol="POP3" />

      {state.kind === 'loading' && (
        <div className="card">
          <div className="card-body" data-testid="tiscalicz-loading">Yükleniyor…</div>
        </div>
      )}

      {state.kind === 'error' && (
        <div className="card">
          <div className="card-body" role="alert" data-testid="tiscalicz-error">
            <span className="badge badge-danger" style={{ marginRight: 8 }}>Hata</span>
            {state.message}
          </div>
        </div>
      )}

      {state.kind === 'ok' && (
        <>
          <div className="kpi-grid" style={{ marginBottom: 16 }}>
              <div className="stat" data-testid="kpi-total">
                <div className="stat-label">Toplam stok</div>
                <div className="stat-value">{state.data.units.total}</div>
                <div className="stat-meta">mailbox (her biri bir unit)</div>
              </div>
              <div className="stat" data-testid="kpi-available">
                <div className="stat-label">Satılabilir</div>
                <div className="stat-value">{state.data.units.available}</div>
                <div className="stat-meta up">stokun %{availableShare(state.data.units)}</div>
              </div>
              <div className="stat" data-testid="kpi-leased">
                <div className="stat-label">Kiralamada</div>
                <div className="stat-value">{state.data.units.leased}</div>
                <div className="stat-meta">şu an müşteride</div>
              </div>
              <div className="stat" data-testid="kpi-verified">
                <div className="stat-label">Kimlik doğrulandı</div>
                <div className="stat-value">{state.data.probe?.verified ?? '—'}</div>
                <div className="stat-meta">
                  {state.data.probe?.pending ?? 0} bekliyor · {state.data.probe?.quarantined ?? 0} karantina
                </div>
              </div>
              <div className="stat" data-testid="kpi-burned-pairs">
                <div className="stat-label">Yanmış hak</div>
                <div className="stat-value">{state.data.units.burnedPairs}</div>
                <div className="stat-meta">(mailbox, site) çifti — kalıcı</div>
              </div>
              <div className="stat" data-testid="kpi-burned-mailboxes">
                <div className="stat-label">Yanmış mailbox</div>
                <div className="stat-value">{state.data.units.mailboxesWithBurns}</div>
                <div className="stat-meta">
                  {state.data.units.total - state.data.units.mailboxesWithBurns} mailbox hiç yanmadı
                </div>
              </div>
              <div className="stat" data-testid="kpi-accounts">
                <div className="stat-label">Hesap</div>
                <div className="stat-value">{state.data.accounts}</div>
                <div className="stat-meta">import edilmiş tiscalicz hesabı</div>
              </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div>
                <div className="card-title">Site başına satış</div>
                <div className="card-subtitle">
                  Hangi site için kaç mailbox satıldı; posta gelince çift kalıcı yanar, gelmezse yalnız o müşteriye kapanır
                </div>
              </div>
              <div className="card-header-actions">
                <span className="badge badge-neutral">{state.data.sites.length} site</span>
              </div>
            </div>
            {state.data.sites.length === 0 ? (
              <div className="card-body" data-testid="tiscalicz-no-sites">
                <span className="badge badge-success" style={{ marginRight: 8 }}>Temiz</span>
                Henüz hiçbir site için satış yok — tüm stok her site için kullanılabilir.
              </div>
            ) : (
              <div className="tbl-wrap" style={{ overflowX: 'auto' }}>
                <table className="tbl" data-testid="tiscalicz-site-table">
                  <thead>
                    <tr>
                      <th>Site</th>
                      <th>Satılan mailbox</th>
                      <th>Yanmış (posta geldi)</th>
                      <th>Satıldı, posta gelmedi</th>
                      <th style={{ minWidth: 200 }}>Stok payı (yanma)</th>
                      <th>Son satış</th>
                      <th>Son yanma</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.data.sites.map((row) => (
                      <tr key={row.site} data-testid={'site-row-' + row.site}>
                        <td className="t-mono" data-label="Site">{row.site}</td>
                        <td data-label="Satılan mailbox" data-testid={'site-sold-' + row.site}>{row.soldMailboxes ?? row.burnedMailboxes + row.servedMailboxes}</td>
                        <td data-label="Yanmış (posta geldi)">{row.burnedMailboxes}</td>
                        <td data-label="Satıldı, posta gelmedi">{row.servedMailboxes}</td>
                        <td className="tiscalicz-share-td" data-label="Stok payı (yanma)">
                          <div className="tiscalicz-share">
                            <span className="tiscalicz-bar" aria-hidden="true">
                              <span
                                className={'tiscalicz-bar-fill' + shareClass(row)}
                                style={{ width: Math.min(100, row.shareOfStock) + '%' }}
                              />
                            </span>
                            <span className="badge badge-neutral" data-testid={'site-share-' + row.site}>
                              %{row.shareOfStock}
                            </span>
                          </div>
                        </td>
                        <td className="t-small t-tertiary" data-label="Son satış">{fmt(row.lastSoldAt)}</td>
                        <td className="t-small t-tertiary" data-label="Son yanma">{fmt(row.lastBurnedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <MailboxUnitManager
            providerId="tiscalicz"
            providerLabel="tiscali.cz"
            onChanged={() => void load()}
          />

          <div className="card">
            <div className="card-body">
              <div className="card-title" style={{ marginBottom: 6 }}>Kapasite</div>
              <p className="tiscalicz-footnote" data-testid="tiscalicz-capacity-note">
                {state.data.units.total} mailbox × sınırsız site hakkı. Şu an{' '}
                <strong>{state.data.units.available}</strong> mailbox satılabilir (%{availableShare(state.data.units)});
                yanmış çiftler yalnızca kendi sitesi için kapalı, mailbox'ın kendisi kapanmaz.
                Import edilen bir mailbox, kimlik doğrulaması (dakikada 10 hesap) geçince satışa açılır.
              </p>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

/** Bar rengi: payı büyüdükçe dikkat çeksin (yeşil → sarı → kırmızı). */
function shareClass(row: TiscaliczSiteUsageRow): string {
  if (row.shareOfStock >= 50) return ' is-high';
  if (row.shareOfStock >= 20) return ' is-mid';
  return '';
}
